import { Box, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Location, useNavigate } from "react-router-dom";
import CustomSnackbar from "./components/common/CustomSnackbar";
import useScreen from "./components/common/UseScreen";
import AppFooter from "./components/layout/app-footer/AppFooter";
import AppHeader from "./components/layout/app-header/AppHeader";
import AppSideMenu from "./components/layout/app-side-menu/AppSideMenu";
import AppRoutes from "./components/routes/AppRoutes";
import { auth } from "./config/firebase";
import { AppBarCategoryItem } from "./data/app-side-menu-list-items";
import LoadingPage from "./pages/LoadingPage";
import {
  LocalStorageKey,
  LocalStorageService,
} from "./services/local-storage.service";
import { UserService } from "./services/user.service";
import { StoreState } from "./store/store";
import { scrollPageToTop, setIsLoading } from "./store/system.store";
import { setUser } from "./store/user.store";
import "./App.css";
import OnboardingModal from "./components/onboarding/OnBoarding";
import { setNotifications } from "./store/notification.store";

const publicRoutes = ["/login", "/initial-password/"];

function App() {
  const theme = useTheme();
  const navigate = useNavigate();
  const appContainerRef = useRef();
  const dispatch = useDispatch();
  const isLoading = useSelector((store: StoreState) => store.system.isLoading);
  const user = useSelector((store: StoreState) => store.user.loggedInUser);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const { isDesktop } = useScreen();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(isDesktop);

  useEffect(() => {
    dispatch(setIsLoading(true));
  }, []);

  useEffect(() => {
    if (user) {
      fetchAllNotifications();
      if (user.role === "admin" || user.role === "agent") {
        navigate("/agent-homepage");
      }
    }
  }, [user]);

  const fetchAllNotifications = async () => {
    if (user?.id) {
      const notifications = await UserService.fetchNotifications();
      if (notifications) {
        dispatch(setNotifications(notifications));
      }
    }
  };

  useEffect(() => {
    if (user) {
      const onboardingShown = LocalStorageService.get(
        LocalStorageKey.ONBOARDING_SHOWN
      );

      if (!onboardingShown && !user.isClient) {
        setShowOnboarding(true);
        LocalStorageService.set(LocalStorageKey.ONBOARDING_SHOWN, true);
      }
    }
    dispatch(setIsLoading(false));
  }, [user]);

  const [selectedDrawerItemPath, setSelectedDrawerItemPath] =
    useState<string>("");

  const onPathChange = (location: Location) => {
    setSelectedDrawerItemPath(location.pathname);
  };

  const sideMenuItemClick = (categoryItem: AppBarCategoryItem) => {
    const path = categoryItem.path || "/";
    setSelectedDrawerItemPath(path);
    setIsDrawerOpen(false);
    dispatch(scrollPageToTop());
    navigate(path);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user && !publicRoutes.includes(window.location.pathname)) {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const onLogout = async () => {
    try {
      await UserService.logout();
      dispatch(setUser(null));
      LocalStorageService.remove(LocalStorageKey.LOGGED_IN_USER);
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <>
      {isLoading && <LoadingPage />}
      <Box>
        {user && (
          <AppSideMenu
            handleItemClick={sideMenuItemClick}
            selectedItemPath={selectedDrawerItemPath}
            handleCloseDrawer={() => setIsDrawerOpen(false)}
            isOpen={isDrawerOpen}
          />
        )}
        <Box
          ref={appContainerRef}
          sx={{
            width:
              isDesktop && user
                ? `calc(100% - ${theme.custom.appDrawerWidth})`
                : "100%",
            position: "absolute",
            marginInlineStart:
              isDesktop && user ? theme.custom.appDrawerWidth : "0",
          }}
        >
          {user && (
            <AppHeader
              handleLogout={onLogout}
              handleOpenDrawer={() => setIsDrawerOpen(true)}
            />
          )}
          <AppRoutes handlePathChange={onPathChange} />
          {user && <AppFooter />}
        </Box>
      </Box>
      {user && showOnboarding && (
        <OnboardingModal
          open={true}
          onClose={() => setShowOnboarding(false)}
          onWhatsAppClick={() =>
            window.open("https://wa.me/19029015915", "_blank")
          }
        />
      )}

      <CustomSnackbar />
    </>
  );
}

export default App;
